import { useState } from 'react';
import { Minus, Plus, RotateCcw, Share2, Volume2 } from 'lucide-react';
import { PERCUSSION, noteName, midiToFreq, findInstrument } from '@/lib/pitch';
import { Panel, nearestIndex } from '@/components/tuner/NoteButton';
import QRShareModal from '@/components/QRShareModal';
import { buildKitShareUrl } from '@/lib/tuningShare';

const storageKey = (id) => `tuner.perc.${id}`;
const load = (id) => {
  const defs = PERCUSSION[id];
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(id)) || 'null');
    if (Array.isArray(saved) && saved.length === defs.length) return saved;
  } catch { /* use defaults */ }
  return defs.map((d) => d.midi);
};

export const savePercNotes = (id, notes) => localStorage.setItem(storageKey(id), JSON.stringify(notes));

const stepBtn = 'h-9 w-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5 disabled:opacity-30 active:scale-95 transition-transform';

const DrumRow = ({ idx, drum, midi, active, target, onPlay, onChange }) => (
  <div
    data-testid={`tuner-drum-${idx}`}
    className={`flex items-center gap-2 rounded-xl border p-2 transition-colors ${active ? 'border-pink-400 bg-pink-500/10' : target ? 'border-emerald-400/70 bg-emerald-400/10' : 'border-white/10'}`}
  >
    <button data-testid={`tuner-drum-${idx}-play`} onClick={onPlay} className="flex-1 min-w-0 flex items-center gap-3 text-left">
      <span className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${active ? 'bg-pink-400 text-black' : 'bg-white/5 text-white/60'}`}>
        <Volume2 className="w-4 h-4" />
      </span>
      <span className="truncate text-sm text-white/80">{drum.label}</span>
    </button>
    <button data-testid={`tuner-drum-${idx}-down`} aria-label="Nota più bassa" disabled={midi <= drum.min} onClick={() => onChange(midi - 1)} className={stepBtn}><Minus className="w-4 h-4" /></button>
    <span data-testid={`tuner-drum-${idx}-note`} className={`w-12 text-center font-bebas text-2xl ${target ? 'text-emerald-300' : ''}`}>{noteName(midi, false)}</span>
    <button data-testid={`tuner-drum-${idx}-up`} aria-label="Nota più alta" disabled={midi >= drum.max} onClick={() => onChange(midi + 1)} className={stepBtn}><Plus className="w-4 h-4" /></button>
  </div>
);

export const PercussionPanel = ({ instrumentId, tone, a4, detected }) => {
  const drums = PERCUSSION[instrumentId];
  const [notes, setNotes] = useState(() => load(instrumentId));
  const [sharing, setSharing] = useState(false);
  const target = nearestIndex(notes, detected);

  const update = (next) => { setNotes(next); savePercNotes(instrumentId, next); };
  const change = (i, m) => {
    update(notes.map((x, j) => (j === i ? m : x)));
    if (tone.playing === `d${i}`) tone.play(`d${i}`, midiToFreq(m, a4));
  };

  return (
    <Panel
      title="Note consigliate · tocca per ascoltare"
      testId="tuner-perc-panel"
      header={(
        <div className="mb-3 flex flex-wrap gap-2">
          <button data-testid="tuner-perc-reset" onClick={() => update(drums.map((d) => d.midi))} className="h-9 px-3 rounded-full border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/60 hover:bg-white/5 inline-flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Ripristina note
          </button>
          <button data-testid="tuner-perc-share" onClick={() => setSharing(true)} className="h-9 px-3 rounded-full border border-sky-400/40 text-[10px] uppercase tracking-[0.2em] text-sky-300 hover:bg-sky-400/10 inline-flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" /> Condividi
          </button>
          <QRShareModal
            open={sharing}
            url={sharing ? buildKitShareUrl(instrumentId, notes) : ''}
            title={findInstrument(instrumentId).name}
            subtitle={drums.map((d, i) => `${d.label} ${noteName(notes[i], false)}`).join(' · ')}
            caption="Scansiona per importare le note dei tamburi"
            onClose={() => setSharing(false)}
          />
        </div>
      )}
    >
      <div className="grid gap-2 w-full sm:grid-cols-2">
        {drums.map((d, i) => (
          <DrumRow
            key={d.id}
            idx={i}
            drum={d}
            midi={notes[i]}
            active={tone.playing === `d${i}`}
            target={target === i}
            onPlay={() => tone.toggle(`d${i}`, midiToFreq(notes[i], a4))}
            onChange={(m) => change(i, m)}
          />
        ))}
      </div>
    </Panel>
  );
};
